import {useEffect,useRef,useState} from 'react';
import {Link} from 'react-router';
import {api,ApiError} from '../api';
import {formatDateTime} from '../dates';
import {ErrorState} from '../ui/State';
type Message={id:string;thread_id:string;subject:string|null;direction:string;occurred_at:string};
type Conversation={id:string;thread_number:string;title:string;messages:Message[]};
export type Correspondence={linked:Conversation[];candidates:Conversation[];decisionMessageId:string|null;canLink:boolean};
export function useEnquiryCorrespondence(id:string|undefined,version:number|undefined){
 const [data,setData]=useState<Correspondence>(),[error,setError]=useState<unknown>();const sequence=useRef(0);
 async function search(q=''){
  if(!id||version===undefined)return;const current=++sequence.current;setError(undefined);
  try{const next=await api<Correspondence>(`/api/enquiries/${id}/correspondence?q=${encodeURIComponent(q)}`);
   if(current===sequence.current)setData(next);
  }catch(e){if(current!==sequence.current)return;if(e instanceof ApiError&&e.statusCode===403)setData(undefined);else setError(e);}
 }
 useEffect(()=>{void search();return()=>{sequence.current++}},[id,version]);
 return {data,error,search};
}
export function DecisionCorrespondence({data}:{data:Correspondence|undefined}){
 const thread=data?.linked.find(t=>t.messages.some(m=>m.id===data.decisionMessageId));
 return thread?<div><dt>Decision correspondence</dt><dd><Link to={`/communications/threads/${thread.id}#message-${data!.decisionMessageId}`}>
  {thread.thread_number} · {thread.messages.find(m=>m.id===data!.decisionMessageId)?.subject??thread.title}</Link></dd></div>:null;
}
export function DecisionHistoryLink({value}:{value:unknown}){
 if(!value||typeof value!=='object')return null;const v=value as Record<string,unknown>;
 return typeof v.threadId==='string'&&typeof v.messageId==='string'&&typeof v.label==='string'?
  <Link to={`/communications/threads/${v.threadId}#message-${v.messageId}`}>{v.label} · Decision message</Link>:null;
}
export function EnquiryDecisionField({data,value,onChange,busy}:{data:Correspondence|undefined;value:string|undefined;onChange:(value:string)=>void;busy:boolean}){
 if(!data)return null;
 return <><label>Decision correspondence (optional)<select disabled={busy} value={value??data.decisionMessageId??'keep'} onChange={e=>onChange(e.target.value)}>
  <option value="keep">Keep the saved source, if any</option><option value="none">No decision correspondence</option>
  {data.linked.map(t=><optgroup key={t.id} label={`${t.thread_number} · ${t.title}`}>
   {t.messages.map(m=><option key={m.id} value={m.id}>{formatDateTime(m.occurred_at)} · {m.direction} · {m.subject??'No subject'}</option>)}
  </optgroup>)}
 </select></label><small>Choose a message you have checked. Link its conversation below if it is missing.</small></>;
}
export function EnquiryCorrespondence({state,busy,onLink}:{state:ReturnType<typeof useEnquiryCorrespondence>;busy:boolean;
 onLink:(body:Record<string,unknown>)=>Promise<boolean>}){
 const {data,error}=state;
 if(!data&&!error)return null;
 return <section className="panel" aria-label="Enquiry correspondence"><h2>Correspondence</h2>
  {Boolean(error)&&<ErrorState error={error}/>}<p>Original messages and attachments stay in Mail. Later replies remain in the linked conversation.</p>
  {data?.linked.length?<ul>{data.linked.map(t=><li key={t.id}><Link to={`/communications/threads/${t.id}`}>
   {t.thread_number} · {t.title}</Link> · {t.messages.length} messages</li>)}</ul>:<p>No accessible conversations are linked.</p>}
  {data?.canLink&&<details><summary>Link an existing conversation</summary>
   <form onSubmit={e=>{e.preventDefault();void state.search(String(new FormData(e.currentTarget).get('q')??''))}}>
    <label>Find conversation<input name="q" required maxLength={120}/></label><button disabled={busy}>Search conversations</button>
   </form>
   {data.candidates.length>0&&<form onSubmit={e=>{e.preventDefault();void onLink({threadId:String(new FormData(e.currentTarget).get('threadId')),
    reason:'Existing conversation explicitly linked to enquiry'});}}>
    <label>Existing conversation<select name="threadId" disabled={busy} required defaultValue=""><option value="" disabled>Choose a conversation</option>
     {data.candidates.map(t=><option key={t.id} value={t.id}>{t.thread_number} · {t.title}</option>)}</select></label>
    <button disabled={busy}>Link conversation</button>
   </form>}
  </details>}
 </section>;
}
